import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import * as faceapi from "face-api.js";
import { loadModels } from "./modelLoader";
import { recognizeFace } from "./models/faceRecognition.module";

// FIREBASE HOOKS
import { useAuthContext } from "../firebase-files/hooks/useAuthContext";

const AttendanceFaceScan = forwardRef(
  (
    {
      showStatus = "",
      height = "",
      width = "",
      borderRadius = "",
      handleCamOpenStatus = () => {},
      onMatch = () => {}, // callback if face matched
      onNoMatch = () => {}, // callback if no match
      onError = () => {}, // callback if face detection fails
      setParentStatus = () => {}, // 👈 function to update status in parent
      setShowRegisterFaceBtn = () => {},
    },
    ref
  ) => {
    const { user } = useAuthContext();

    const videoRef = useRef(null);
    const [matched, setMatched] = useState(false);
    const [isScanLine, setScanLine] = useState(false);

    // Load models + start video
    useEffect(() => {
      const start = async () => {
        await loadModels();
        startVideo();
      };
      start();
    }, []);

    const startVideo = () => {
      navigator.mediaDevices
        .getUserMedia({ video: true })

        .then((stream) => {
          videoRef.current.srcObject = stream;
          console.log("🎥 Camera stream started");

          handleCamOpenStatus();
        })
        .catch((err) => {
          console.error("Webcam error:", err);
        });
    };

    const handleScan = async () => {
      setScanLine(true);
      setParentStatus("🔍 Scanning for face...");

      try {
        const liveDescriptor = await recognizeFace(videoRef.current);

        if (!liveDescriptor || liveDescriptor.length !== 128) {
          const errorSound = new Audio("/assets/sounds/error.mp3");
          errorSound.play();
          onNoMatch();
          setParentStatus("❌ Face not detected");
          setScanLine(false);
          return;
        }

        const validLiveDescriptor = new Float32Array(liveDescriptor);

        let foundMatch = null;

        // ✅ Compare only with logged-in user's face descriptors
        const registeredFaces = (user?.faceRegistration || []).filter(
          (item) =>
            item &&
            Array.isArray(item.faceDescriptor) &&
            item.faceDescriptor.length === 128
        );

        if (registeredFaces.length === 0) {
          const errorSound = new Audio("/assets/sounds/error.mp3");
          errorSound.play();
          setParentStatus("❌ No registered face found");
          setShowRegisterFaceBtn(true);
          setScanLine(false);
          return;
        }

        for (const descriptorData of registeredFaces) {
          const storedDescriptor = new Float32Array(
            descriptorData.faceDescriptor
          );
          const distance = faceapi.euclideanDistance(
            validLiveDescriptor,
            storedDescriptor
          );

          if (distance < 0.5) {
            foundMatch = {
              fullName: user?.fullName || "You",
              id: user?.uid || "",
              ...user,
            };
            break;
          }
        }

        if (foundMatch) {
          const successSound = new Audio("/assets/sounds/success.mp3");
          successSound.play();
          setMatched(true);
          setParentStatus(`Hii ${foundMatch.fullName.split(" ")[0]} 👋`);
          onMatch(foundMatch);
        } else {
          const errorSound = new Audio("/assets/sounds/error.mp3");
          errorSound.play();
          setParentStatus("❌ Face not recognized");
          onNoMatch();
        }

        setScanLine(false);
      } catch (err) {
        const errorSound = new Audio("/assets/sounds/error.mp3");
        errorSound.play();
        setParentStatus("⚠️ Something went wrong");
        onError(err);
        setScanLine(false);
      }
    };

    // 👇 expose handleScan to parent
    useImperativeHandle(ref, () => ({
      triggerScan: () => handleScan(),
    }));

    return (
      <div style={{ position: "relative", width: width, height: height }}>
        <video
          ref={videoRef}
          autoPlay
          muted
          style={{
            width: width,
            height: height,
            borderRadius: borderRadius,
            objectFit: "cover",
          }}
        />
        {/* Blue scanning overlay */}
        {isScanLine && (
          <div className="face-scan-overlay">
            <div className="scan-line" />
          </div>
        )}
      </div>
    );
  }
);
export default AttendanceFaceScan;
